import React, { useEffect, useMemo, useRef, useState } from "react";

import { Download, Filter, Loader2, Truck, X } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useProductDetails } from "~/hooks/useProductDetails";
import { supabase } from "~/integrations/supabase/client";
import { downloadFacturaWithDocuments } from "~/services/documentDownloadService";
import { SalesRequest, SalesRequestItem } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";
import { getDisplayId } from "~/utils/salesRequestDisplayUtils";

interface LogisticaPorFacturarTableProps {
  salesRequests: SalesRequest[];
  clientNames: Record<string, string>;
  salesRequestItems: Record<string, SalesRequestItem[]>;
  getSellerInfo?: (sellerId: string) => {
    name: string;
    lastname: string;
    email: string;
  };
  onOrderClick: (order: SalesRequest) => void;
  onAssignToTransportista: (selectedOrderIds: string[]) => void;
  fetchItemsForSalesRequest: (salesRequestId: string) => Promise<void>;
}

interface OrderTotals {
  totalNetAmount: number;
  totalWeight: number;
  totalVolume: number;
}

export const LogisticaPorFacturarTable: React.FC<
  LogisticaPorFacturarTableProps
> = ({
  salesRequests,
  clientNames,
  salesRequestItems,
  getSellerInfo,
  onOrderClick,
  onAssignToTransportista,
  fetchItemsForSalesRequest,
}) => {
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(
    new Set()
  );
  const [clientTaxIds, setClientTaxIds] = useState<Record<string, string>>({});
  const [downloadingOrderId, setDownloadingOrderId] = useState<string | null>(
    null
  );
  const [regionFilter, setRegionFilter] = useState<string>("");
  const [comunaFilter, setComunaFilter] = useState<string>("");
  const [cityFilter, setCityFilter] = useState<string>("");
  const fetchedItemsRef = useRef<Set<string>>(new Set());
  const fetchedClientIdsRef = useRef<Set<string>>(new Set());
  const fetchedProductIdsRef = useRef<Set<string>>(new Set());

  // Create a stable reference for por_facturar orders only
  const porFacturarOrdersBase = useMemo(() => {
    return salesRequests.filter((order) => order.status === "por_facturar");
  }, [
    salesRequests
      .filter((order) => order.status === "por_facturar")
      .map(
        (order) =>
          `${order.id}-${order.status}-${order.updatedAt || order.createdAt}`
      )
      .join(","),
  ]);

  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    const regions = [
      ...new Set(
        porFacturarOrdersBase
          .map((order) => order.deliveryState)
          .filter(Boolean)
      ),
    ].sort();
    const comunas = [
      ...new Set(
        porFacturarOrdersBase
          .map((order) => order.deliveryComuna)
          .filter(Boolean)
      ),
    ].sort();
    const cities = [
      ...new Set(
        porFacturarOrdersBase.map((order) => order.deliveryCity).filter(Boolean)
      ),
    ].sort();

    return { regions, comunas, cities };
  }, [porFacturarOrdersBase]);

  // Filter por_facturar orders based on delivery address filters
  const porFacturarOrders = useMemo(() => {
    let filtered = porFacturarOrdersBase;

    // Apply region filter
    if (regionFilter) {
      filtered = filtered.filter(
        (order) => order.deliveryState === regionFilter
      );
    }

    // Apply comuna filter
    if (comunaFilter) {
      filtered = filtered.filter(
        (order) => order.deliveryComuna === comunaFilter
      );
    }

    // Apply city filter
    if (cityFilter) {
      filtered = filtered.filter((order) => order.deliveryCity === cityFilter);
    }

    return filtered;
  }, [porFacturarOrdersBase, regionFilter, comunaFilter, cityFilter]);

  // Create a stable reference for client IDs to prevent unnecessary re-fetching
  const clientIds = useMemo(() => {
    const ids = [
      ...new Set(porFacturarOrdersBase.map((order) => order.clientId)),
    ];
    return ids.sort(); // Sort to ensure stable reference
  }, [porFacturarOrdersBase]);

  // Automatically fetch items for all por_facturar orders
  useEffect(() => {
    const ordersNeedingItems = porFacturarOrdersBase.filter(
      (order) =>
        !fetchedItemsRef.current.has(order.id) && !salesRequestItems[order.id]
    );

    ordersNeedingItems.forEach((order) => {
      fetchedItemsRef.current.add(order.id);
      fetchItemsForSalesRequest(order.id);
    });
  }, [porFacturarOrdersBase.length]); // Only depend on the number of por_facturar orders

  // Get all unique product IDs from sales request items - use a more stable approach
  const productIds = useMemo(() => {
    const ids = new Set<string>();

    porFacturarOrdersBase.forEach((order) => {
      const items = salesRequestItems[order.id] || [];
      items.forEach((item) => {
        ids.add(item.productId);
      });
    });

    // Sort to ensure stable reference
    return Array.from(ids).sort();
  }, [porFacturarOrdersBase, salesRequestItems]);

  // Use the product details hook with stable product IDs
  const { productDetails, isLoading: isLoadingProducts } =
    useProductDetails(productIds);

  // Fetch client tax IDs - only when we have new client IDs
  useEffect(() => {
    const fetchClientTaxIds = async () => {
      if (clientIds.length === 0) return;

      // Only fetch client IDs that we haven't fetched before
      const newClientIds = clientIds.filter(
        (id) => !fetchedClientIdsRef.current.has(id)
      );
      if (newClientIds.length === 0) return;

      try {
        const { data, error } = await supabase
          .from("clients")
          .select("id, tax_id")
          .in("id", newClientIds);

        if (error) {
          console.error("Error fetching client tax IDs:", error);
          return;
        }

        const taxIds: Record<string, string> = {};
        data?.forEach((client) => {
          taxIds[client.id] = client.tax_id || "N/A";
          fetchedClientIdsRef.current.add(client.id);
        });
        setClientTaxIds((prev) => ({ ...prev, ...taxIds }));
      } catch (error) {
        console.error("Failed to fetch client tax IDs:", error);
      }
    };

    fetchClientTaxIds();
  }, [clientIds.join(",")]); // Use a string representation for stable comparison

  // Calculate totals for selected orders
  const selectedTotals = useMemo((): OrderTotals => {
    let totalNetAmount = 0;
    let totalWeight = 0;
    let totalVolume = 0;

    selectedOrderIds.forEach((orderId) => {
      const order = porFacturarOrders.find((o) => o.id === orderId);
      if (!order) return;

      // Add net amount (without IVA)
      totalNetAmount += order.totalAmount;

      // Use direct weight/volume if available, otherwise calculate from items
      if (order.weight_kg !== undefined && order.volume_m3 !== undefined) {
        totalWeight += order.weight_kg;
        totalVolume += order.volume_m3;
      } else {
        // Fallback: Calculate weight and volume from items
        const items = salesRequestItems[orderId] || [];
        items.forEach((item) => {
          const product = productDetails[item.productId];
          if (product) {
            totalWeight += (product.weight_kg || 0) * item.quantity;
            totalVolume +=
              ((product.volume_cm3 || 0) * item.quantity) / 1000000; // Convert cm³ to m³
          }
        });
      }
    });

    return {
      totalNetAmount,
      totalWeight,
      totalVolume,
    };
  }, [selectedOrderIds, porFacturarOrders, salesRequestItems, productDetails]);

  // Handle individual checkbox change
  const handleOrderSelection = (orderId: string, checked: boolean) => {
    const newSelection = new Set(selectedOrderIds);
    if (checked) {
      newSelection.add(orderId);
    } else {
      newSelection.delete(orderId);
    }
    setSelectedOrderIds(newSelection);
  };

  // Handle downloading a single order's factura + related documents
  const handleDownloadDocuments = async (order: SalesRequest) => {
    setDownloadingOrderId(order.id);
    try {
      await downloadFacturaWithDocuments(
        order,
        clientNames[order.clientId] || "Cliente"
      );
    } finally {
      setDownloadingOrderId(null);
    }
  };

  // Handle select all checkbox
  const handleSelectAll = (checked: string | boolean) => {
    if (checked) {
      setSelectedOrderIds(new Set(porFacturarOrders.map((order) => order.id)));
    } else {
      setSelectedOrderIds(new Set());
    }
  };

  // Get delivery address for an order
  const getDeliveryAddress = (order: SalesRequest) => {
    if (order.deliveryStreet || order.deliveryNumber) {
      return `${order.deliveryStreet || ""} ${order.deliveryNumber || ""}`.trim();
    }
    return "Dirección no especificada";
  };

  // Simplified approach: use direct weight/volume from sales request if available, otherwise calculate
  const getOrderWeightAndVolumeDisplay = (order: SalesRequest) => {
    // Check if the order has direct weight/volume properties (new approach)
    if (
      order.weight_kg !== undefined &&
      order.volume_m3 !== undefined &&
      (order.weight_kg > 0 || order.volume_m3 > 0)
    ) {
      return {
        weight: order.weight_kg.toFixed(2),
        volume: order.volume_m3.toFixed(3),
      };
    }

    // Fallback to calculation from items (old approach)
    const items = salesRequestItems[order.id] || [];

    if (items.length === 0) {
      return { weight: "0.00", volume: "0.000" };
    }

    let totalWeight = 0;
    let totalVolume = 0;
    let hasAnyProductData = false;

    items.forEach((item) => {
      const product = productDetails[item.productId];
      if (product) {
        hasAnyProductData = true;
        totalWeight += (product.weight_kg || 0) * item.quantity;
        totalVolume += (product.volume_cm3 || 0) * item.quantity;
      }
    });

    // If we don't have product data yet, return loading
    if (!hasAnyProductData && items.length > 0) {
      return { weight: "Cargando...", volume: "Cargando..." };
    }

    return {
      weight: totalWeight.toFixed(2),
      volume: (totalVolume / 1000000).toFixed(3), // Convert cm³ to m³
    };
  };

  // Check if all orders are selected
  const isAllSelected =
    porFacturarOrders.length > 0 &&
    selectedOrderIds.size === porFacturarOrders.length;
  const isIndeterminate =
    selectedOrderIds.size > 0 &&
    selectedOrderIds.size < porFacturarOrders.length;

  if (porFacturarOrders.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notas Por Facturar</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center text-muted-foreground">
            No hay notas de venta listas para facturar
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="mb-4 flex items-center justify-between">
            <CardTitle>Filtros de Dirección de Entrega</CardTitle>
            {(regionFilter || comunaFilter || cityFilter) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setRegionFilter("");
                  setComunaFilter("");
                  setCityFilter("");
                }}
              >
                <X className="mr-2 h-4 w-4" />
                Limpiar Filtros
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-2 block text-sm font-medium">Región</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
              >
                <option value="">Todas las regiones</option>
                {filterOptions.regions.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">Comuna</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={comunaFilter}
                onChange={(e) => setComunaFilter(e.target.value)}
              >
                <option value="">Todas las comunas</option>
                {filterOptions.comunas.map((comuna) => (
                  <option key={comuna} value={comuna}>
                    {comuna}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">Ciudad</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
              >
                <option value="">Todas las ciudades</option>
                {filterOptions.cities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Header with totals and action button */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              Notas Por Facturar ({porFacturarOrders.length})
              {(regionFilter || comunaFilter || cityFilter) && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  (filtradas)
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-4">
              {selectedOrderIds.size > 0 && (
                <>
                  <div className="text-sm text-muted-foreground">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <span className="font-medium">Total:</span>
                        <br />
                        {formatCurrency(selectedTotals.totalNetAmount)}
                      </div>
                      <div>
                        <span className="font-medium">Peso:</span>
                        <br />
                        {selectedTotals.totalWeight.toFixed(2)} kg
                      </div>
                      <div>
                        <span className="font-medium">Volumen:</span>
                        <br />
                        {selectedTotals.totalVolume.toFixed(3)} m³
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() =>
                      onAssignToTransportista(Array.from(selectedOrderIds))
                    }
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Truck className="mr-2 h-4 w-4" />
                    Asignar a Transportista ({selectedOrderIds.size})
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Fecha Emisión</TableHead>
                <TableHead>Fecha Entrega</TableHead>
                <TableHead>Vendedor</TableHead>
                <TableHead>Dirección de Entrega</TableHead>
                <TableHead>Comuna</TableHead>
                <TableHead>Región</TableHead>
                <TableHead>Peso (kg)</TableHead>
                <TableHead>Volumen (m³)</TableHead>
                <TableHead>Monto</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {porFacturarOrders.map((order, index) => {
                const isSelected = selectedOrderIds.has(order.id);
                const seller = getSellerInfo?.(order.createdBy);

                return (
                  <TableRow
                    key={order.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className={`duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both ${isSelected ? "bg-blue-50" : ""}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) =>
                          handleOrderSelection(order.id, !!checked)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="link"
                        className="h-auto p-0 text-left"
                        onClick={() => onOrderClick(order)}
                      >
                        {clientNames[order.clientId] || "Cliente sin nombre"}
                      </Button>
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(order.createdAt).toLocaleDateString("es-CL")}
                    </TableCell>
                    <TableCell className="text-sm">
                      {order.deliveryDate
                        ? new Date(order.deliveryDate).toLocaleDateString(
                            "es-CL"
                          )
                        : order.requestedDeliveryDateStart
                          ? new Date(
                              order.requestedDeliveryDateStart
                            ).toLocaleDateString("es-CL")
                          : "Por definir"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {seller ? `${seller.name} ${seller.lastname}` : "N/A"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {getDeliveryAddress(order)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {order.deliveryComuna || "N/A"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {order.deliveryState || "N/A"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {(() => {
                        const display = getOrderWeightAndVolumeDisplay(order);
                        return display.weight;
                      })()}
                    </TableCell>
                    <TableCell className="text-sm">
                      {(() => {
                        const display = getOrderWeightAndVolumeDisplay(order);
                        return display.volume;
                      })()}
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      {formatCurrency(order.totalAmount)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadDocuments(order)}
                        disabled={downloadingOrderId === order.id}
                        className="flex items-center gap-1"
                        title="Descargar factura y documentos relacionados"
                      >
                        {downloadingOrderId === order.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        {downloadingOrderId === order.id
                          ? "Descargando..."
                          : "Descargar"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
